import { ReactComponent as ArrowDown } from "assets/ArrowDown.svg";
import { ReactComponent as ArrowUp } from "assets/ArrowUp.svg";
import { cloneDeep, isEmpty, isInteger, isString, isUndefined } from "lodash";
import { useState } from "react";
import { tokens } from "styles/Tokens";
import { getTableColor, noDataFound } from "utils/CommonFunctions";
import "./MobileCommonTable.scss";
import Typography from "./Typography";

const MobileCommonTable = (props: any) => {
  const [childData, setChildData] = useState<any>([]);
  const [isRowOpen, setIsRowOpen] = useState<any>([]);

  const handleClick = (index: number, newData: string) => {
    // Update the state of `childData` using the previous state
    setChildData((prevStates: any) => {
      // Find the index of the item in the array with the given key
      const existingIndex = prevStates.findIndex((item: any) => item.key === index);

      if (existingIndex !== -1) {
        // If the item exists, update its `data` while keeping other properties unchanged
        return prevStates.map((item: any) => (item.key === index ? { ...item, data: newData } : item));
      }

      // If the item does not exist, add a new object with the key and newData
      return [...prevStates, { key: index, data: newData }];
    });
  };

  const handleSetChildRowData = (info: any, index: any) => {
    // Create a deep clone of the current `isRowOpen` state to avoid mutating state directly
    const clonedIsRowOpen = cloneDeep(isRowOpen);

    // Find if an entry with the given index already exists in the cloned state
    const item = clonedIsRowOpen?.find((item: any) => item.key === index);

    if (item) {
      // If found, toggle its `isOpen` state
      item.isOpen = !item.isOpen; // Mutating the object directly inside cloned array
    } else {
      // If not found, add a new entry with `isOpen` set to `true`
      clonedIsRowOpen.push({ key: index, isOpen: true });
    }

    // Update the state with the modified cloned array
    setIsRowOpen(clonedIsRowOpen);

    // Call an external handler function with index and info
    handleClick(index, info);
  };

  const getColor = (val: any) => {
    if (val.color) {
      return val.color;
    }
    if (isString(val?.content)) {
      const color = getTableColor(val?.content);
      if (isUndefined(color)) {
        return tokens.Text.General.PrimaryBody;
      }
      return color;
    }
    return tokens.Text.General.PrimaryBody;
  };

  const mapHeadersToContent = (key: number) => {
    // Find the corresponding data in childData based on the key
    const findData = childData.find((item: any) => item.key === key);

    // Filter headers to remove any with empty content
    const nonEmptyHeaders = props.data.headers.filter((item: any) => item.content.trim() !== "");
    // Map through the filtered headers to generate UI elements
    return nonEmptyHeaders.map((data: any, index: number) => {
      if (data.content) {
        // Ensure content is not empty
        // Get the corresponding content for this header index
        const content = findData?.data?.data?.[index];
        return (
          <div className="grid-2fr-table">
            {/* First column with header content */}
            <div className="grid-2fr-item">
              <Typography variant="body2" color={tokens.Text.General.PrimaryHeadline}>
                {data.content}
              </Typography>
            </div>

            {/* Second column: if first index has main element skip it */}
            {index !== 0 && content?.tooltip && !data.ignoreTooltip ? (
              <div className="grid-2fr-item">
                {content.tooltip?.list ? (
                  content.tooltip?.list?.map((item: any) => {
                    return (
                      <div className="tooltip-wrapper" style={{ padding: "4px 0px", color: getTableColor(item?.name) }}>
                        <Typography variant={"body2"} component="span" showBold>
                          {item.value < 10 ? "0" : ""}
                          {`${item.value ?? ""}  `}
                        </Typography>
                        {item?.name} {item?.isShowStar > 0 ? "*" : ""}
                      </div>
                    );
                  })
                ) : !isUndefined(content.tooltip?.title) ? (
                  <Typography variant={"body2"} color="#312F2E">
                    {content.tooltip?.title}
                  </Typography>
                ) : (
                  "-"
                )}
              </div>
            ) : (
              // Otherwise, display the content or a fallback "-"
              <div className="grid-2fr-item">
                <Typography variant="body2" color={getColor(content)}>
                  {content.isCheckboxHidden
                    ? content.value
                    : !isEmpty(content.content) || isInteger(content.content)
                    ? content?.content
                    : "-"}
                </Typography>
              </div>
            )}
          </div>
        );
      }
    });
  };

  return (
    <div className="common-mobile-table">
      {props?.data?.rows?.length ? (
        <div className="common-mobile-table-container">
          {props?.data?.rows.map((rowDatas: any, index: any) => {
            // Find if the row with the given index is open
            const isRowExpanded = isRowOpen?.find((item: any) => item.key === index)?.isOpen;

            // Filter headers to exclude items with empty content
            const nonEmptyHeaders = props.data.headers.filter((item: any) => item.content.trim() !== "");

            // Check if the number of rows (`rowDatas.data.length`) is greater than the number of headers
            // This can be used to determine if there are additional actions available in the rows
            const hasExtraRows = rowDatas.data > nonEmptyHeaders;

            const lastIndex = rowDatas.data.length;

            return (
              <>
                <div className="content-wrapper">
                  <div className="content-header-container">
                    <div className="icon-name-wrapper">
                      <div className="up-down-icon" onClick={() => handleSetChildRowData(rowDatas, index)}>
                        {isRowExpanded ? <ArrowUp /> : <ArrowDown />}
                      </div>
                      <div className={props?.tableName === "adminConsole" ? "ellipse-three" : "ellipse-two"}>
                        <Typography variant="body2" color={tokens.Text.General.PrimaryHeadline} showBold>
                          {rowDatas.data[0].content}
                        </Typography>
                      </div>
                    </div>
                    {hasExtraRows && <div className="action-main">{rowDatas.data?.[lastIndex - 1]?.content}</div>}
                  </div>
                </div>
                {isRowExpanded && <div className="collapse-content">{mapHeadersToContent(index)}</div>}
              </>
            );
          })}
        </div>
      ) : (
        <div className="horizontal-center">{noDataFound(props?.data?.headers?.length, "No Results")}</div>
      )}
    </div>
  );
};

export default MobileCommonTable;
